import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchPaymentConfig, updatePaymentConfig } from '../../services/paymentConfigService';

const Page = styled.div`
  max-width: 760px;
`;

const Header = styled.div`
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 28px;
  padding-bottom: 22px;
`;

const Eyebrow = styled.p`
  color: var(--color-accent);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.2em;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  color: var(--color-text);
  font-family: var(--font-serif);
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-semibold);
`;

const Panel = styled.form`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: 24px;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 22px;
`;

const Label = styled.span`
  color: var(--color-text-secondary);
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.14em;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const Input = styled.input`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  color: var(--color-text);
  font-size: var(--font-size-md);
  padding: 12px;
  width: 100%;

  &:focus {
    border-color: var(--color-accent);
    outline: 2px solid var(--color-accent-light);
  }
`;

const Help = styled.p`
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
  margin-top: 8px;
`;

const Feedback = styled.div`
  background: ${({ $error }) => ($error ? 'rgba(185, 28, 28, 0.08)' : 'rgba(22, 101, 52, 0.08)')};
  border: 1px solid ${({ $error }) => ($error ? 'rgba(185, 28, 28, 0.22)' : 'rgba(22, 101, 52, 0.22)')};
  border-radius: var(--border-radius-sm);
  color: ${({ $error }) => ($error ? 'var(--color-error)' : 'var(--color-success)')};
  margin-bottom: 18px;
  padding: 12px 14px;
`;

const Button = styled.button`
  background: var(--color-primary);
  border: 0;
  border-radius: var(--border-radius-sm);
  color: white;
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  padding: 12px 18px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

function paymentConfigToForm(config) {
  return {
    commissionPercent: (Number(config.artistCommissionBps) / 100).toFixed(2),
    serviceFee: (Number(config.buyerServiceFeeFixedPence) / 100).toFixed(2),
    reviewWindowDays: String(config.fulfilmentReviewWindowDays),
  };
}

const PaymentSettings = () => {
  const [values, setValues] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchPaymentConfig()
      .then((config) => {
        if (active) setValues(paymentConfigToForm(config));
      })
      .catch((err) => {
        if (active) setError(err.message || 'Unable to load payment settings.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadAttempt]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!values || saving) return;
    const commission = Number(values.commissionPercent);
    const fee = Number(values.serviceFee);
    const reviewDays = Number(values.reviewWindowDays);

    if (
      Object.values(values).some((value) => value.trim() === '') ||
      !Number.isFinite(commission) ||
      commission < 0 ||
      commission > 100 ||
      !Number.isFinite(fee) ||
      fee < 0 ||
      !Number.isInteger(reviewDays) ||
      reviewDays < 1 ||
      reviewDays > 30
    ) {
      setError('Enter valid payment settings. Review window must be 1 to 30 days.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');
      const config = await updatePaymentConfig({
        artistCommissionBps: Math.round(commission * 100),
        buyerServiceFeeFixedPence: Math.round(fee * 100),
        fulfilmentReviewWindowDays: reviewDays,
      });
      setValues(paymentConfigToForm(config));
      setMessage('Payment settings saved.');
    } catch (err) {
      setError(err.message || 'Unable to save payment settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <Header>
        <Eyebrow>Settings</Eyebrow>
        <Title>Payment settings</Title>
      </Header>

      {error && <Feedback $error role="alert">{error}</Feedback>}
      {message && <Feedback role="status">{message}</Feedback>}

      {loading && <p role="status">Loading payment settings...</p>}
      {!loading && !values && (
        <Button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
          Retry loading settings
        </Button>
      )}
      {!loading && values && (
        <Panel onSubmit={handleSubmit}>
          <Field>
            <Label>Artist commission percent</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              required
              disabled={saving}
              aria-label="Artist commission percent"
              value={values.commissionPercent}
              onChange={(event) => setValues({ ...values, commissionPercent: event.target.value })}
            />
            <Help>Applied to the artist base price only when the artist is not subscribed.</Help>
          </Field>

          <Field>
            <Label>Buyer service fee GBP</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              required
              disabled={saving}
              aria-label="Buyer service fee GBP"
              value={values.serviceFee}
              onChange={(event) => setValues({ ...values, serviceFee: event.target.value })}
            />
            <Help>Added on top of base price and delivery for collectors.</Help>
          </Field>

          <Field>
            <Label>Fulfilment review window days</Label>
            <Input
              type="number"
              min="1"
              max="30"
              step="1"
              required
              disabled={saving}
              aria-label="Fulfilment review window days"
              value={values.reviewWindowDays}
              onChange={(event) => setValues({ ...values, reviewWindowDays: event.target.value })}
            />
            <Help>Sets the dispatch deadline and artist drop-off review period. Shipped orders always retain a separate 14-day inspection period from confirmed delivery.</Help>
          </Field>

          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </Button>
        </Panel>
      )}
    </Page>
  );
};

export default PaymentSettings;
